// inputSanitizer.js - Smart input cleaning utility with BALANCED validation

/**
 * Cleans and normalizes user input before sending to backend/API
 * Removes extra whitespace, normalizes unicode, strips invisible chars
 * @param {string} text - Raw user input
 * @param {object} options - Cleaning options
 * @returns {string} - Cleaned text
 */
export function cleanUserInput(text, options = {}) {
  const {
    preserveNewlines = false,
    maxConsecutiveSpaces = 1,
    removeInvisibleChars = true,
    normalizeUnicode = true,
    trimWhitespace = true
  } = options;

  if (!text || typeof text !== 'string') return '';

  let cleaned = text;

  if (normalizeUnicode) {
    cleaned = cleaned.normalize("NFKC");
  }

  if (removeInvisibleChars) {
    cleaned = cleaned.replace(/[\u200B-\u200D\uFEFF\u00A0]/g, '');
  }

  if (!preserveNewlines) {
    cleaned = cleaned.replace(/\n+/g, ' ');
  } else {
    cleaned = cleaned.replace(/\n{3,}/g, '\n\n');
  }

  const spaceRegex = maxConsecutiveSpaces === 1 
    ? /\s+/g 
    : new RegExp(`\\s{${maxConsecutiveSpaces + 1},}`, 'g');
  cleaned = cleaned.replace(spaceRegex, ' '.repeat(maxConsecutiveSpaces));

  if (trimWhitespace) {
    cleaned = cleaned.trim();
  }

  return cleaned;
}

/**
 * Validates cleaned input for presentation topics
 * SIMPLIFIED: Only blocks obvious spam, allows legitimate content
 * @param {string} text - Cleaned text
 * @param {object} options - Validation options
 * @returns {object} - { isValid, reason }
 */
export function validatePromptInput(text, options = {}) {
  const {
    allowShort = false,
    minLength = 3,
    maxLength = 5000  // ✅ FIXED: Now matches UI limit
  } = options;

  const trimmed = text.trim();

  // Basic length checks
  if (!allowShort && trimmed.length < minLength) {
    return { isValid: false, reason: 'TOO_SHORT' };
  }

  if (trimmed.length > maxLength) {
    return { isValid: false, reason: 'TOO_LONG' };
  }

  // Empty or only whitespace
  if (trimmed.length === 0) {
    return { isValid: false, reason: 'NO_CONTENT' };
  }

  // ✅ SIMPLIFIED: Only block OBVIOUS spam patterns

  // 1. Must have at least one letter
  if (!/[a-zA-Z]/.test(trimmed)) {
    return { isValid: false, reason: 'NO_WORDS' };
  }

  // 2. Block only EXTREME repeated characters (aaaaaaaaaa, 1111111111)
  // Must be 8+ same characters in a row to be blocked
  if (/(.)\1{7,}/.test(trimmed)) {
    return { isValid: false, reason: 'REPEATED_CHAR' };
  }

  // 3. Block obvious keyboard spam (must be exact patterns)
  if (/^(asdf)+$|^(qwerty)+$|^(zxcv)+$|^(hjkl)+$/i.test(trimmed)) {
    return { isValid: false, reason: 'KEYBOARD_SPAM' };
  }

  // 4. Block if ENTIRE input is just special characters (no alphanum at all)
  if (/^[^a-zA-Z0-9]+$/.test(trimmed)) {
    return { isValid: false, reason: 'NO_CONTENT' };
  }

  // ✅ REMOVED: All the aggressive checks
  // - Consonant cluster check (was blocking "Machine", "Script", "Strength")
  // - Vowel ratio check (was blocking "HTTP", "XML", "SQL")
  // - Repeated character percentage (was blocking "coffee", "Mississippi")
  // - Single word check (was blocking "Photosynthesis", "Democracy")
  // - Special character ratio (was blocking emails, URLs)

  return { isValid: true };
}